import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  bulkUpdateActionPlanStatus,
  createActionPlan,
  deleteActionPlan as deleteActionPlanApi,
  deleteActionPlanEvidence,
  getActionPlanResultInputOptions,
  listActionPlanResults,
  rejectActionPlanResult,
  searchActionPlans,
  uploadActionPlanEvidence,
  upsertActionPlanResult,
  updateActionPlan,
  verifyActionPlanResult,
} from '../../../api/climateActionPlanApi';
import {
  normalizeActionPlan, normalizeActionPlanResult, normalizeActionPlanResultInputOptions,
  serializeActionPlanDraft,
} from '../components/actionPlanModel';

const isCanceled = error => (
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
);

/** Load and mutate action plans for the selected fiscal year from the backend. */
export default function useClimateActionPlanMaster() {
  const [actionPlans, setActionPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadSequence, setReloadSequence] = useState(0);
  const sequenceRef = useRef(0);

  const load = useCallback((filters = {}) => {
    const controller = new AbortController();
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    setLoading(true);
    setError(null);

    searchActionPlans(filters, { signal: controller.signal })
      .then((payload) => {
        if (sequenceRef.current !== sequence) return;
        setActionPlans(Array.isArray(payload?.action_plans)
          ? payload.action_plans.map(normalizeActionPlan) : []);
        setTotal(Number(payload?.total) || 0);
      })
      .catch((loadError) => {
        if (sequenceRef.current === sequence && !isCanceled(loadError)) {
          setError(loadError);
        }
      })
      .finally(() => {
        if (sequenceRef.current === sequence) setLoading(false);
      });

    return controller;
  }, []);

  useEffect(() => {
    const controller = load({});
    return () => controller.abort();
  }, [load, reloadSequence]);

  const reload = useCallback(() => {
    setReloadSequence(sequence => sequence + 1);
  }, []);

  const saveActionPlan = useCallback(async (draft, planId = null) => {
    const payload = serializeActionPlanDraft(draft);
    const response = planId
      ? await updateActionPlan(planId, payload)
      : await createActionPlan(payload);
    return {
      plan: normalizeActionPlan(response),
      similarActions: Array.isArray(response?.similar_actions)
        ? response.similar_actions
        : [],
    };
  }, []);

  const deleteActionPlan = useCallback(async (planId) => {
    await deleteActionPlanApi(planId);
    setReloadSequence(sequence => sequence + 1);
  }, []);

  const bulkTransitionActionPlans = useCallback(async (planIds, targetStatus) => {
    const response = await bulkUpdateActionPlanStatus({
      action_ids: planIds,
      target_status: targetStatus,
    });
    setReloadSequence(sequence => sequence + 1);
    return response;
  }, []);

  const loadActionPlanResults = useCallback(async (planId) => {
    const response = await listActionPlanResults(planId);
    return (Array.isArray(response) ? response : response?.results || []).map(normalizeActionPlanResult);
  }, []);

  const loadActionPlanResultInputOptions = useCallback(async (planId, fiscalYear) => (
    normalizeActionPlanResultInputOptions(await getActionPlanResultInputOptions(planId, fiscalYear))
  ), []);

  const saveActionPlanResult = useCallback(async (planId, fiscalYear, payload, status) => {
    const response = await upsertActionPlanResult(planId, fiscalYear, payload);
    const saved = normalizeActionPlanResult(response);
    if (status === 'VERIFIED' && saved.id && saved.status !== 'VERIFIED') {
      return normalizeActionPlanResult(await verifyActionPlanResult(saved.id, {
        note: payload.note || null,
      }));
    }
    if (status === 'REJECTED' && saved.id && saved.status !== 'REJECTED') {
      return normalizeActionPlanResult(await rejectActionPlanResult(saved.id, {
        note: payload.note || null,
      }));
    }
    return saved;
  }, []);

  const uploadEvidence = useCallback(async (resultId, files) => {
    const response = await uploadActionPlanEvidence(resultId, files);
    return response;
  }, []);

  const deleteEvidence = useCallback(async (evidenceId) => {
    await deleteActionPlanEvidence(evidenceId);
  }, []);

  const actionPlansByYear = useMemo(() => actionPlans, [actionPlans]);

  return {
    actionPlans: actionPlansByYear,
    total,
    loading,
    error,
    reload,
    load,
    saveActionPlan,
    deleteActionPlan,
    bulkTransitionActionPlans,
    loadActionPlanResults,
    loadActionPlanResultInputOptions,
    saveActionPlanResult,
    uploadEvidence,
    deleteEvidence,
  };
}
